import * as React from 'react'
import {
  applyBranchSwitch,
  useActiveBranch,
  useAppDispatch
} from '@axilon-apps/domains-ontology-ui-state'

import {
  abandonIngestWizardDraft,
  refreshIngestWizardDraft,
  resumeIngestWizardDraft,
  skipIngestWizardDraftSource,
  startIngestWizardDraft
} from './wizardPersistence'
import {
  resolveIngestWizardBranchSwitch,
  type WizardBranchObservation
} from './wizardBranchAdoption'
import type { IngestWizardApi } from './useIngestWizard'

const POLL_MS = 2_000

/**
 * Observes and commands a server-owned wizard run. Source launch, job waiting,
 * retries, and queue progression deliberately do not run in the browser.
 */
export function useHeadlessLaunch(wizard: IngestWizardApi) {
  const { draftId, queueStarted, run } = wizard.state
  const activeBranchId = useActiveBranch()
  const dispatch = useAppDispatch()
  const busyRef = React.useRef(false)
  const [commandError, setCommandError] = React.useState<string | null>(null)
  const running = queueStarted && !run.paused

  const applyBranchObservation = React.useCallback(
    (observation: WizardBranchObservation) => {
      const next = resolveIngestWizardBranchSwitch({
        runId: draftId,
        activeBranchId,
        observation
      })
      if (next.shouldSwitch) {
        // Ingest begin already opened the branch. Adopt it without a redundant
        // :open request; failures restore the branch this tab left.
        applyBranchSwitch(dispatch, next.branchId)
      }
    },
    [activeBranchId, dispatch, draftId]
  )

  React.useEffect(() => {
    if (!run.branchId) return
    applyBranchObservation({
      branchId: run.branchId,
      outcome: run.pauseReason === 'error' ? 'failed' : 'active'
    })
  }, [applyBranchObservation, run.branchId, run.pauseReason])

  // The wizard draft isn't a job and publishes no wake-up, so a launch stays on a deliberate poll.
  React.useEffect(() => {
    if (!draftId || (!queueStarted && !running)) return
    let disposed = false
    const refresh = async (): Promise<void> => {
      try {
        applyBranchObservation(await refreshIngestWizardDraft(draftId))
      } catch {
        // A transient read must not change durable run state. The next poll
        // retries, while command failures are surfaced to their callers.
      }
    }
    void refresh()
    const timer = window.setInterval(() => {
      if (!disposed) void refresh()
    }, POLL_MS)
    return () => {
      disposed = true
      window.clearInterval(timer)
    }
  }, [applyBranchObservation, draftId, queueStarted, running])

  const command = React.useCallback(
    async (fn: (id: string) => Promise<WizardBranchObservation | void>) => {
      if (!draftId || busyRef.current) return
      busyRef.current = true
      setCommandError(null)
      try {
        const observation = await fn(draftId)
        if (observation) applyBranchObservation(observation)
      } catch (error) {
        setCommandError(error instanceof Error ? error.message : String(error))
      } finally {
        busyRef.current = false
      }
    },
    [applyBranchObservation, draftId]
  )

  return {
    statuses: run.statuses,
    errors: run.errors,
    jobIdsBySource: run.jobIdsBySource,
    notesBySource: run.notesBySource ?? {},
    running,
    paused: run.paused,
    pausedSourceId: run.pausedSourceId,
    pauseReason: run.pauseReason,
    branchId: run.branchId,
    mappingBranchId: run.mappingBranchId,
    groupId: run.groupId,
    commandError,
    runQueue: () => command(startIngestWizardDraft),
    resumeQueue: () => command(resumeIngestWizardDraft),
    skipPausedAndResume: () => command(skipIngestWizardDraftSource),
    reset: () => command(abandonIngestWizardDraft)
  }
}
